import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios'; // Assuming axios is available
import { FaSpinner } from 'react-icons/fa'; // Import spinner icon

const Planning = () => {
  const { t } = useTranslation();
  // Removed task-related functions from useAppContext as we are replacing them
  const { wedding, setWedding } = useAppContext();
  const navigate = useNavigate();

  // State for planner items
  const [plannerItems, setPlannerItems] = useState([]);
  const [newItemName, setNewItemName] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemName, setEditingItemName] = useState('');
  const [editingItemDescription, setEditingItemDescription] = useState('');

  const cardId = localStorage.getItem('id');

  // Fetch planner items
  const fetchPlannerItems = async () => {
    if (!cardId) {
      console.warn('No card ID found in localStorage. Cannot fetch planner items.');
      return; // Don't fetch if no cardId
    }
    setIsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/planner/planner-items`, {
        params: { card_id: cardId },
      });
      setPlannerItems(response.data);
    } catch (error) {
      console.error('Error fetching planner items:', error);
      // Optionally show an error message to the user
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlannerItems();
  }, [cardId]); // Re-fetch if cardId changes

  // Add new planner item
  const handleAddPlannerItem = async (e) => {
    e.preventDefault();
    if (!newItemName.trim() || !cardId) return;

    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/planner/planner-items`, {
        card_id: cardId,
        name: newItemName,
        description: newItemDescription,
      });
      setPlannerItems([...plannerItems, response.data]);
      setNewItemName('');
      setNewItemDescription('');
      // Show success message using translation
    } catch (error) {
      console.error('Error adding planner item:', error);
      // Optionally show an error message to the user
    } finally {
      setIsLoading(false);
    }
  };

  // Edit planner item
  const handleEditPlannerItem = (item) => {
    setEditingItemId(item.id);
    setEditingItemName(item.name);
    setEditingItemDescription(item.description || '');
  };

  const handleSavePlannerItem = async (id) => {
    if (!editingItemName.trim() || !cardId) return;

    setIsLoading(true);
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/planner/planner-items/${id}`, {
        card_id: cardId, // Include card_id if the API requires it for update
        name: editingItemName,
        description: editingItemDescription,
      });
      setPlannerItems(plannerItems.map(item => (item.id === id ? response.data : item)));
      setEditingItemId(null);
      setEditingItemName('');
      setEditingItemDescription('');
    } catch (error) {
      console.error('Error updating planner item:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditingItemName('');
    setEditingItemDescription('');
  };

  // Delete planner item
  const handleDeletePlannerItem = async (id) => {
    if (!cardId) return;
    if (window.confirm(t('planning.plannerItemDeleteConfirmation') || 'Are you sure you want to delete this planner item?')) {
      setIsLoading(true);
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/planner/planner-items/${id}`);
        setPlannerItems(plannerItems.filter(item => item.id !== id));
      } catch (error) {
        console.error('Error deleting planner item:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Clear all planner items
  const handleClearPlannerItems = async () => {
    if (!cardId) return;
    if (window.confirm(t('planning.plannerItemsClearConfirmation'))) {
      setIsLoading(true);
      try {
        // The backend API for clearing uses DELETE on /planner/planner-items and expects card_id
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/planner/planner-items`, {
          data: { card_id: cardId } // Sending card_id in the body for DELETE request
        });
        setPlannerItems([]);
        alert(t('planning.plannerItemsCleared'));
      } catch (error) {
        console.error('Error clearing planner items:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleToggleComplete = async (item) => {
    const updatedItem = { ...item, completed: !item.completed };
    try {
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/planner/planner-items/${item.id}`, updatedItem);
      setPlannerItems(plannerItems.map(i => (i.id === item.id ? updatedItem : i)));
    } catch (error) {
      console.error('Error updating planner item:', error);
    }
  };

  // Disable clear button if no items or loading
  const isClearButtonDisabled = plannerItems.length === 0 || isLoading;

  const sortedPlannerItems = [...plannerItems].sort((a, b) => a.completed - b.completed);

  return (
    <div className="w-full max-w-4xl mx-auto py-10 px-2 sm:px-4 overflow-x-hidden">
      <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2">{t('planning.title')}</h1>
      <p className="text-foreground/70 mb-2">{t('planning.description')}</p>

      {/* New Planner Item Form */}
      <div className="flex flex-wrap md:flex-nowrap gap-3 mb-6">
        <form onSubmit={handleAddPlannerItem} className="flex flex-col md:flex-row md:flex-wrap gap-3 flex-1 min-w-0">
          <input
            className="flex-1 border rounded px-3 py-2 text-base sm:text-lg min-w-[150px] w-full"
            placeholder={t('planning.plannerItemNamePlaceholder')}
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            required
          />
          <input
            className="flex-1 border rounded px-3 py-2 text-base sm:text-lg min-w-[150px] w-full"
            placeholder={t('planning.plannerItemDescriptionPlaceholder')}
            value={newItemDescription}
            onChange={(e) => setNewItemDescription(e.target.value)}
          />
          <button
            type="submit"
            disabled={!newItemName.trim() || isLoading || !cardId}
            className={`bg-primary text-white px-4 py-2 rounded hover:bg-secondary hover:text-gray-800 transition-colors w-full sm:w-auto ${
              (!newItemName.trim() || isLoading || !cardId) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {t('common.add')}
          </button>
        </form>

        <button
          onClick={handleClearPlannerItems}
          disabled={isClearButtonDisabled}
          className={`bg-danger text-white px-4 py-2 rounded hover:bg-secondary hover:text-gray-800 transition-colors w-full md:w-auto ${
            isClearButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {t('planning.clearAllTasks')} {/* Reusing clearAllTasks for planner items clear */}
        </button>
      </div>

      {/* Planner Items List */}
      <ul className="space-y-2">
        {isLoading && (
          <li className="text-foreground/60 italic">
            <FaSpinner className="animate-spin mr-2" />
            {t('common.loading')}
          </li>
        )}
        {!isLoading && plannerItems.length === 0 && cardId && (
          <li className="text-foreground/60 italic">{t('planning.noPlannerItemsYet')}</li>
        )}
        {!isLoading && !cardId && (
          <li className="text-danger italic">Please ensure you have a valid ID in localStorage.</li>
        )}
        {!isLoading && sortedPlannerItems.map((item) => (
          <li
            key={item.id}
            className="flex flex-wrap items-center gap-3 bg-white rounded shadow px-4 py-2"
          >
            {editingItemId === item.id ? (
              <>
                <input
                  className="flex-1 border rounded px-2 py-1 min-w-[150px]"
                  value={editingItemName}
                  onChange={(e) => setEditingItemName(e.target.value)}
                  autoFocus
                />
                <input
                  className="flex-1 border rounded px-2 py-1 min-w-[150px]"
                  value={editingItemDescription}
                  onChange={(e) => setEditingItemDescription(e.target.value)}
                />
                <button
                  onClick={() => handleSavePlannerItem(item.id)}
                  className="text-success font-bold px-2"
                >
                  {t('common.save')}
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="text-danger px-2"
                >
                  {t('common.cancel')}
                </button>
              </>
            ) : (
              <>
                <input
                  type="checkbox"
                  checked={item.completed}
                  onChange={() => handleToggleComplete(item)}
                  className="form-checkbox h-5 w-5 text-primary"
                />
                <div className="flex-1 min-w-[150px]">
                  <p className={`font-semibold ${item.completed ? 'line-through' : ''}`}>{item.name}</p>
                  {item.description && <p className="text-sm text-foreground/70">{item.description}</p>}
                </div>
                <button
                  onClick={() => handleEditPlannerItem(item)}
                  className="text-info px-2"
                >
                  {t('common.edit')}
                </button>
                <button
                  onClick={() => handleDeletePlannerItem(item.id)}
                  className="text-danger px-2"
                >
                  {t('common.delete')}
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Planning;
